import { useState, useCallback, useEffect } from 'react';

interface HoverPreviewProps {
  url: string;
  x: number;
  y: number;
  show: boolean;
}

const HoverPreview: React.FC<HoverPreviewProps> = ({ url, x, y, show }) => {
  if (!show || !url) return null;

  return (
    <div
      className='fixed z-50 rounded-md overflow-hidden shadow-lg border border-gray-700'
      style={{
        left: `${x}px`,
        top: `${y}px`,
        maxWidth: '300px',
        maxHeight: '200px',
        backgroundColor: '#212a40',
      }}
    >
      <div className='relative w-full h-full min-h-[100px] min-w-[100px] flex items-center justify-center'>
        <div className='absolute inset-0 flex items-center justify-center'>
          <div className='h-8 w-8 border-2 border-t-transparent border-blue-500 rounded-full animate-spin'></div>
        </div>

        <img
          src={url}
          alt='Preview'
          className='w-full h-full object-contain relative z-10'
          onLoad={e => {
            const parent = (e.target as HTMLElement).parentElement;
            if (parent) {
              const loader = parent.querySelector('div');
              if (loader) loader.style.display = 'none';
            }
          }}
          onError={e => {
            const imgElement = e.target as HTMLImageElement;
            imgElement.onerror = null;
            imgElement.src = 'https://via.placeholder.com/300x150?text=Invalid+Image+URL';

            const parent = imgElement.parentElement;
            if (parent) {
              const loader = parent.querySelector('div');
              if (loader) loader.style.display = 'none';
            }
          }}
        />
      </div>
    </div>
  );
};

export const useHoverPreview = (debounceTime = 100) => {
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewProps>({
    show: false,
    x: 0,
    y: 0,
    url: '',
  });

  const [previewDebounce, setPreviewDebounce] = useState<NodeJS.Timeout | null>(null);

  const isValidURL = (url: string): boolean => {
    if (!url || url.trim() === '') return false;
    try {
      return url.startsWith('http://') || url.startsWith('https://');
    } catch (e) {
      return false;
    }
  };

  // Handle mouse move on input with debouncing
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>, url: string) => {
      if (previewDebounce) {
        clearTimeout(previewDebounce);
      }

      const timeout = setTimeout(() => {
        if (isValidURL(url)) {
          setHoverPreview({
            show: true,
            x: e.clientX + 10,
            y: e.clientY + 10,
            url,
          });
        }
      }, debounceTime);

      setPreviewDebounce(timeout);
    },
    [previewDebounce, debounceTime]
  );

  const handleMouseLeave = useCallback(() => {
    if (previewDebounce) {
      clearTimeout(previewDebounce);
      setPreviewDebounce(null);
    }
    setHoverPreview(prev => ({ ...prev, show: false }));
  }, [previewDebounce]);

  useEffect(() => {
    return () => {
      if (previewDebounce) {
        clearTimeout(previewDebounce);
      }
    };
  }, [previewDebounce]);

  return {
    hoverPreview,
    handleMouseMove,
    handleMouseLeave,
    HoverPreviewComponent: <HoverPreview {...hoverPreview} />,
  };
};

export default HoverPreview;
